import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Search, Filter, X, UserPlus, ChevronRight, Loader2,
  Calendar, UserCircle2, Phone, Briefcase, Wrench, Package, Landmark,
  CheckCircle2, XCircle, RefreshCw, Trash2, Edit3, ArrowRightCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";

/* -------------------------------------------------------------------- */
/*                              Constants                               */
/* -------------------------------------------------------------------- */
const STATUSES = [
  { v: "all",                label: "All Statuses" },
  { v: "new",                label: "New" },
  { v: "in_progress",        label: "In Progress" },
  { v: "follow_up_required", label: "Follow-up Required" },
  { v: "converted",          label: "Converted" },
  { v: "not_interested",     label: "Not Interested" },
  { v: "closed",             label: "Closed" },
];
const TYPES = [
  { v: "all",         label: "All Types" },
  { v: "purchase",    label: "Purchase",     icon: Package },
  { v: "service",     label: "Service",      icon: Wrench },
  { v: "spare_parts", label: "Spare Parts",  icon: Briefcase },
  { v: "subsidy",     label: "Subsidy",      icon: Landmark },
  { v: "other",       label: "Other" },
];
const SOURCES = [
  { v: "all",               label: "All Sources" },
  { v: "walk_in",           label: "Walk-in" },
  { v: "phone",             label: "Phone" },
  { v: "reference",         label: "Reference" },
  { v: "existing_customer", label: "Existing Customer" },
  { v: "other",             label: "Other" },
];
const CUST_TYPES = [
  { v: "all",                   label: "All Customer Types" },
  { v: "existing_customer",     label: "Existing Customer" },
  { v: "prospective_customer",  label: "Prospective Customer" },
];

const STATUS_STYLE = {
  new:                { bg: "bg-[#2d4a5e]/40", text: "text-sky-200",     border: "border-sky-500/40" },
  in_progress:        { bg: "bg-[#5e4a2d]/40", text: "text-amber-200",   border: "border-amber-500/40" },
  follow_up_required: { bg: "bg-[#5e2d4a]/40", text: "text-fuchsia-200", border: "border-fuchsia-500/40" },
  converted:          { bg: "bg-[#1a3524]",    text: "text-[#39FF14]",   border: "border-[#39FF14]/40" },
  not_interested:     { bg: "bg-[#2d2d2d]",    text: "text-slate-400",   border: "border-slate-500/30" },
  closed:             { bg: "bg-[#2d2d2d]",    text: "text-slate-300",   border: "border-slate-500/40" },
};
const CUST_TYPE_STYLE = {
  existing_customer:   "bg-[#1a3524] text-[#39FF14] border-[#39FF14]/40",
  prospective_customer:"bg-[#5e4a2d]/40 text-amber-200 border-amber-500/40",
};

/* -------------------------------------------------------------------- */
/*                           Small helpers                              */
/* -------------------------------------------------------------------- */
const titleCase = (s) => (s || "")
  .replace(/_/g, " ")
  .replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

function StatusPill({ status }) {
  const st = STATUS_STYLE[status] || STATUS_STYLE.new;
  return (
    <Badge
      data-testid={`enquiry-status-pill-${status}`}
      className={`${st.bg} ${st.text} ${st.border} rounded-md font-medium`}
      variant="outline"
    >
      {titleCase(status)}
    </Badge>
  );
}
function CustomerTypePill({ type }) {
  if (!type) return null;
  return (
    <Badge
      data-testid={`enquiry-cust-type-${type}`}
      className={`${CUST_TYPE_STYLE[type]} rounded-md font-medium`}
      variant="outline"
    >
      {titleCase(type.replace("_customer", ""))}
    </Badge>
  );
}

/* -------------------------------------------------------------------- */
/*                     Inline customer search / create                  */
/* -------------------------------------------------------------------- */
function CustomerPicker({ value, onChange, onCreateProspect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/search", { params: { q, limit: 8 } });
        if (!active) return;
        setResults(data?.items || data || []);
      } catch (e) { if (active) setResults([]); }
      finally { if (active) setLoading(false); }
    }, 220);
    return () => { active = false; clearTimeout(timer); };
  }, [q]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-3 p-3 rounded-[10px] bg-[#121815] border border-[#39FF14]/40">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <UserCircle2 size={16} className="text-[#39FF14] shrink-0" />
            <span className="font-medium text-[#F8F9FA]" data-testid="enq-selected-customer-name">
              {value.name}
            </span>
            {value.customer_no && (
              <span className="text-xs text-[#8A8F98] font-mono">{value.customer_no}</span>
            )}
          </div>
          <div className="text-xs text-[#8A8F98] mt-1 flex items-center gap-2">
            <Phone size={12} /> {value.phone || "—"}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)} data-testid="enq-clear-customer">
          <X size={14} /> Change
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
        <Input
          placeholder="Search by name, mobile, or customer ID…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="pl-9 bg-[#121815] border-[#3A4640]"
          data-testid="enq-customer-search-input"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#8A8F98]" />}
      </div>
      {open && q.length >= 2 && (
        <div className="rounded-[10px] bg-[#121815] border border-[#3A4640] max-h-[260px] overflow-y-auto">
          {results.length === 0 && !loading && (
            <div className="p-4 text-sm text-[#8A8F98] text-center">
              <div>No customers found.</div>
              <Button
                variant="outline" size="sm"
                className="mt-3 border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10"
                onClick={() => { setOpen(false); onCreateProspect(q); }}
                data-testid="enq-create-prospect-btn"
              >
                <UserPlus size={14} className="mr-1.5" /> Create "{q}" as Prospective Customer
              </Button>
            </div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => { setOpen(false); onChange(c); }}
              className="w-full text-left px-3 py-2 hover:bg-[#1A1F1C] border-b border-[#3A4640] last:border-b-0 transition-colors"
              data-testid={`enq-customer-option-${c.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-[#F8F9FA] truncate">{c.name}</div>
                  <div className="text-xs text-[#8A8F98] mt-0.5 flex items-center gap-2">
                    <Phone size={11} /> {c.phone || "—"}
                    {c.customer_no && <span className="font-mono">· {c.customer_no}</span>}
                  </div>
                </div>
                <ChevronRight size={14} className="text-[#8A8F98]" />
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="text-xs text-[#8A8F98] px-1">
        Don't see them? Start typing a name and click <span className="text-[#39FF14]">Create Prospective Customer</span>.
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*                     Prospective Customer modal                        */
/* -------------------------------------------------------------------- */
function ProspectiveCustomerModal({ open, defaultName, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(defaultName || ""); setPhone(""); setLocation(""); }
  }, [open, defaultName]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!phone.trim() || phone.replace(/\D/g, "").length < 7) {
      toast.error("A valid mobile number is required"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/enquiries/customers/quick",
        { name: name.trim(), phone: phone.trim(), location: location.trim() });
      if (data?.customer) {
        toast.success(data?.created
          ? "Prospective customer created"
          : "Customer with this mobile already exists");
        onCreated(data.customer);
        onClose();
      }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} className="text-[#39FF14]" /> Create Prospective Customer
          </DialogTitle>
          <DialogDescription className="text-[#8A8F98]">
            Minimum info to proceed. You can complete the profile from the Customers page later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="bg-[#121815] border-[#3A4640] mt-1"
                   data-testid="prospect-name-input" />
          </div>
          <div>
            <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Mobile *</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="98XXXXXXXX"
                   className="bg-[#121815] border-[#3A4640] mt-1"
                   data-testid="prospect-phone-input" />
          </div>
          <div>
            <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Location</label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)}
                   placeholder="Village / City"
                   className="bg-[#121815] border-[#3A4640] mt-1"
                   data-testid="prospect-location-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="prospect-cancel-btn">Cancel</Button>
          <Button
            onClick={save} disabled={saving}
            className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
            data-testid="prospect-save-btn"
          >
            {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Create & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------- */
/*                        New / Edit Enquiry modal                      */
/* -------------------------------------------------------------------- */
function EnquiryFormModal({ open, assignees, onClose, onSaved, initial }) {
  const [customer, setCustomer] = useState(null);
  const [source, setSource] = useState("walk_in");
  const [enqType, setEnqType] = useState("purchase");
  const [category, setCategory] = useState("");
  const [model, setModel] = useState("");
  const [notes, setNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [followDate, setFollowDate] = useState("");
  const [followNotes, setFollowNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [showProspect, setShowProspect] = useState(false);
  const [prospectName, setProspectName] = useState("");

  useEffect(() => {
    if (open) {
      setCustomer(initial?.customer || null);
      setSource(initial?.source || "walk_in");
      setEnqType(initial?.enquiry_type || "purchase");
      setCategory(initial?.product_category || "");
      setModel(initial?.product_model || "");
      setNotes(initial?.notes || "");
      setAssignedTo(initial?.assigned_to || "");
      setFollowDate(initial?.follow_up_date || "");
      setFollowNotes(initial?.follow_up_notes || "");
    }
  }, [open, initial]);

  const save = async () => {
    if (!customer?.id) { toast.error("Please select or create a customer"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: customer.id, source, enquiry_type: enqType,
        product_category: category, product_model: model, notes,
        assigned_to: assignedTo || "", follow_up_date: followDate || "",
        follow_up_notes: followNotes,
      };
      const { data } = await api.post("/enquiries", payload);
      toast.success(`Enquiry ${data?.enquiry?.enquiry_no} created`);
      onSaved(data?.enquiry);
      onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA] max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={18} className="text-[#39FF14]" /> New Enquiry
            </DialogTitle>
            <DialogDescription className="text-[#8A8F98]">
              Attach an enquiry to an existing or prospective customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <label className="text-xs text-[#8A8F98] uppercase tracking-wider mb-1 block">Customer *</label>
              <CustomerPicker
                value={customer}
                onChange={setCustomer}
                onCreateProspect={(name) => { setProspectName(name); setShowProspect(true); }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Source *</label>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger className="bg-[#121815] border-[#3A4640] mt-1" data-testid="enq-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121815] border-[#3A4640]">
                    {SOURCES.filter(s=>s.v!=="all").map((s) => (
                      <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Type *</label>
                <Select value={enqType} onValueChange={setEnqType}>
                  <SelectTrigger className="bg-[#121815] border-[#3A4640] mt-1" data-testid="enq-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121815] border-[#3A4640]">
                    {TYPES.filter(t=>t.v!=="all").map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Product Category</label>
                <Input value={category} onChange={(e) => setCategory(e.target.value)}
                       placeholder="e.g. Power Weeder"
                       className="bg-[#121815] border-[#3A4640] mt-1"
                       data-testid="enq-category-input" />
              </div>
              <div>
                <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Product / Model</label>
                <Input value={model} onChange={(e) => setModel(e.target.value)}
                       placeholder="e.g. MH 710"
                       className="bg-[#121815] border-[#3A4640] mt-1"
                       data-testid="enq-model-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Notes</label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="What did the customer ask about?"
                        className="bg-[#121815] border-[#3A4640] mt-1"
                        data-testid="enq-notes-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Assigned To</label>
                <Select value={assignedTo || "unassigned"} onValueChange={(v) => setAssignedTo(v === "unassigned" ? "" : v)}>
                  <SelectTrigger className="bg-[#121815] border-[#3A4640] mt-1" data-testid="enq-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="bg-[#121815] border-[#3A4640] max-h-[240px]">
                    <SelectItem value="unassigned">— Unassigned —</SelectItem>
                    {assignees.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email} <span className="text-[#8A8F98] text-xs">· {titleCase(u.role)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Follow-up Date</label>
                <Input type="date" value={followDate} onChange={(e) => setFollowDate(e.target.value)}
                       className="bg-[#121815] border-[#3A4640] mt-1"
                       data-testid="enq-follow-up-date-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Follow-up Notes</label>
              <Input value={followNotes} onChange={(e) => setFollowNotes(e.target.value)}
                     placeholder="(optional)"
                     className="bg-[#121815] border-[#3A4640] mt-1"
                     data-testid="enq-follow-up-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} data-testid="enq-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving}
                    className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
                    data-testid="enq-save-btn">
              {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              Create Enquiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ProspectiveCustomerModal
        open={showProspect}
        defaultName={prospectName}
        onClose={() => setShowProspect(false)}
        onCreated={(c) => setCustomer(c)}
      />
    </>
  );
}

/* -------------------------------------------------------------------- */
/*                          Detail Drawer                               */
/* -------------------------------------------------------------------- */
function EnquiryDrawer({ enqId, onClose, onChanged, assignees, canFull }) {
  const [enq, setEnq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'delete'|'convert' }
  const [newStatus, setNewStatus] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newFollow, setNewFollow] = useState("");
  const [newFollowNotes, setNewFollowNotes] = useState("");

  const load = useCallback(async () => {
    if (!enqId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/enquiries/${enqId}`);
      setEnq(data?.enquiry || null);
      setNewStatus(data?.enquiry?.status || "");
      setNewAssignee(data?.enquiry?.assigned_to || "");
      setNewFollow(data?.enquiry?.follow_up_date || "");
      setNewFollowNotes(data?.enquiry?.follow_up_notes || "");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [enqId]);

  useEffect(() => { load(); }, [load]);

  if (!enqId) return null;

  const act = async (fn, successMsg) => {
    setSaving(true);
    try { await fn(); if (successMsg) toast.success(successMsg); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const applyStatus = () => act(async () => {
    await api.post(`/enquiries/${enqId}/status`, { status: newStatus });
  }, "Status updated");

  const applyAssignee = () => act(async () => {
    await api.post(`/enquiries/${enqId}/assign`, { assigned_to: newAssignee });
  }, "Assignment updated");

  const applyFollow = () => act(async () => {
    await api.post(`/enquiries/${enqId}/follow-up`, {
      follow_up_date: newFollow, follow_up_notes: newFollowNotes,
    });
  }, "Follow-up updated");

  const convertNow = () => act(async () => {
    await api.post(`/enquiries/${enqId}/convert`);
  }, "Marked as Converted");

  const deleteNow = () => act(async () => {
    await api.delete(`/enquiries/${enqId}`);
    toast.success("Enquiry deleted");
    onClose();
  });

  return (
    <Sheet open={!!enqId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="bg-[#0B0F0C] border-l border-[#3A4640] text-[#F8F9FA] w-full sm:max-w-[560px] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-[#F8F9FA]">
            {enq?.enquiry_no || "Enquiry"}
            {enq && <StatusPill status={enq.status} />}
          </SheetTitle>
          <SheetDescription className="text-[#8A8F98]">
            Created {enq?.created_at ? formatDate(enq.created_at) : "—"}
            {enq?.created_by_name && ` · by ${enq.created_by_name}`}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-10 flex items-center justify-center text-[#8A8F98]">
            <Loader2 className="animate-spin" />
          </div>
        )}

        {enq && (
          <div className="space-y-4 mt-4" data-testid="enquiry-drawer-content">
            {/* Customer */}
            <section className="p-3 rounded-[10px] bg-[#121815] border border-[#3A4640]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCircle2 size={16} className="text-[#39FF14]" />
                  <span className="font-medium">{enq.customer_name || "—"}</span>
                  <CustomerTypePill type={enq.customer_type_derived} />
                </div>
                {enq.customer_id && (
                  <a href={`/customers/${enq.customer_id}`}
                     className="text-xs text-[#39FF14] hover:underline flex items-center gap-1"
                     data-testid="enq-open-customer-link">
                    Open <ArrowRightCircle size={12} />
                  </a>
                )}
              </div>
              <div className="text-xs text-[#8A8F98] mt-1 flex items-center gap-2">
                <Phone size={11}/> {enq.customer_phone || "—"}
                {enq.customer_no && <span className="font-mono">· {enq.customer_no}</span>}
              </div>
            </section>

            {/* Enquiry details */}
            <section className="p-3 rounded-[10px] bg-[#121815] border border-[#3A4640] space-y-2">
              <Row k="Source" v={titleCase(enq.source)} />
              <Row k="Type"   v={titleCase(enq.enquiry_type)} />
              <Row k="Product Category" v={enq.product_category || "—"} />
              <Row k="Product / Model"  v={enq.product_model    || "—"} />
              <Row k="Notes" v={enq.notes || "—"} />
            </section>

            {/* Quick actions */}
            <section className="p-3 rounded-[10px] bg-[#121815] border border-[#3A4640] space-y-3">
              <div className="text-xs uppercase tracking-wider text-[#8A8F98]">Change Status</div>
              <div className="flex gap-2">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-[#0B0F0C] border-[#3A4640]" data-testid="drawer-status-select"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-[#121815] border-[#3A4640]">
                    {STATUSES.filter(s=>s.v!=="all").map((s) => (
                      <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={applyStatus} disabled={saving || newStatus === enq.status}
                        className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
                        data-testid="drawer-status-apply">Apply</Button>
              </div>

              <div className="text-xs uppercase tracking-wider text-[#8A8F98] pt-2">Assign</div>
              <div className="flex gap-2">
                <Select value={newAssignee || "unassigned"} onValueChange={(v) => setNewAssignee(v === "unassigned" ? "" : v)}>
                  <SelectTrigger className="bg-[#0B0F0C] border-[#3A4640]" data-testid="drawer-assign-select"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                  <SelectContent className="bg-[#121815] border-[#3A4640] max-h-[220px]">
                    <SelectItem value="unassigned">— Unassigned —</SelectItem>
                    {assignees.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name || u.email} <span className="text-xs text-[#8A8F98]">· {titleCase(u.role)}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={applyAssignee} disabled={saving || !canFull || newAssignee === (enq.assigned_to||"")}
                        className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
                        data-testid="drawer-assign-apply">
                  Apply
                </Button>
              </div>
              {!canFull && <div className="text-xs text-[#8A8F98]">Only managers can reassign.</div>}

              <div className="text-xs uppercase tracking-wider text-[#8A8F98] pt-2">Follow-up</div>
              <div className="flex gap-2">
                <Input type="date" value={newFollow} onChange={(e) => setNewFollow(e.target.value)}
                       className="bg-[#0B0F0C] border-[#3A4640]"
                       data-testid="drawer-follow-date-input" />
                <Button onClick={applyFollow} disabled={saving}
                        className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
                        data-testid="drawer-follow-apply">Set</Button>
              </div>
              <Input placeholder="Follow-up notes" value={newFollowNotes}
                     onChange={(e) => setNewFollowNotes(e.target.value)}
                     className="bg-[#0B0F0C] border-[#3A4640]"
                     data-testid="drawer-follow-notes-input" />

              <div className="flex gap-2 pt-3">
                <Button
                  onClick={convertNow} disabled={saving || enq.status === "converted"}
                  className="flex-1 bg-[#1a3524] text-[#39FF14] border border-[#39FF14]/40 hover:bg-[#39FF14]/10"
                  data-testid="drawer-convert-btn"
                >
                  <CheckCircle2 size={14} className="mr-1.5" /> Mark Converted
                </Button>
                {canFull && (
                  <Button
                    onClick={() => setConfirm({ kind: "delete" })}
                    disabled={saving}
                    variant="ghost"
                    className="text-red-300 hover:text-red-400 hover:bg-red-500/10"
                    data-testid="drawer-delete-btn"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </section>

            {/* Activity */}
            <section className="p-3 rounded-[10px] bg-[#121815] border border-[#3A4640]">
              <div className="text-xs uppercase tracking-wider text-[#8A8F98] mb-2">Activity</div>
              <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                {(enq.activity_log || []).slice().reverse().map((a) => (
                  <div key={a.id} className="text-xs text-[#F8F9FA]/90 border-l-2 border-[#39FF14]/40 pl-2">
                    <div>{a.description}</div>
                    <div className="text-[#8A8F98] mt-0.5">
                      {a.actor_name || "—"} · {formatDate(a.at)}
                    </div>
                  </div>
                ))}
                {!(enq.activity_log || []).length && (
                  <div className="text-xs text-[#8A8F98]">No activity yet.</div>
                )}
              </div>
            </section>
          </div>
        )}

        <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
          <DialogContent className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA]">
            <DialogHeader>
              <DialogTitle>Delete this enquiry?</DialogTitle>
              <DialogDescription className="text-[#8A8F98]">
                This hides the enquiry from the list. The customer timeline entry remains.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                className="bg-red-500 hover:bg-red-600"
                onClick={() => { setConfirm(null); deleteNow(); }}
                data-testid="drawer-delete-confirm"
              >
                <Trash2 size={14} className="mr-1.5" /> Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="w-32 text-xs uppercase tracking-wider text-[#8A8F98] mt-0.5">{k}</div>
      <div className="flex-1 min-w-0 text-[#F8F9FA] break-words">{v}</div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*                             Main page                                */
/* -------------------------------------------------------------------- */
export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [assignees, setAssignees] = useState([]);

  const [f, setF] = useState({
    q: "", status: "all", customer_type: "all", enquiry_type: "all",
    source: "all", assigned_to: "all", date_from: "", date_to: "",
  });

  const canFull = useMemo(() => {
    const stored = localStorage.getItem("user");
    try {
      const u = stored ? JSON.parse(stored) : null;
      return [
        "admin","co_admin","owner","sales_manager","marketing_head","service_manager",
      ].includes((u?.role || "").toLowerCase());
    } catch { return false; }
  }, []);

  const loadAssignees = useCallback(async () => {
    try {
      const { data } = await api.get("/enquiries/meta/assignable-users");
      setAssignees(data?.items || []);
    } catch { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (f.q) params.q = f.q;
      ["status","customer_type","enquiry_type","source","assigned_to"].forEach((k) => {
        if (f[k] && f[k] !== "all") params[k] = f[k];
      });
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to)   params.date_to   = f.date_to;
      const { data } = await api.get("/enquiries", { params });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [page, limit, f]);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/enquiries/stats"); setStats(data); }
    catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadAssignees(); }, [loadAssignees]);
  useEffect(() => { load(); loadStats(); }, [load, loadStats]);

  const clearFilters = () => setF({
    q: "", status: "all", customer_type: "all", enquiry_type: "all",
    source: "all", assigned_to: "all", date_from: "", date_to: "",
  });

  return (
    <div className="p-6 space-y-6" data-testid="enquiries-page">
      <PageHeader
        title="Enquiries"
        subtitle="Central workspace for all customer enquiries — existing or prospective."
        action={
          <Button
            onClick={() => setShowNew(true)}
            className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
            data-testid="new-enquiry-btn"
          >
            <Plus size={14} className="mr-1.5" /> New Enquiry
          </Button>
        }
      />

      {/* Stat chips */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {STATUSES.filter(s => s.v !== "all").map((s) => (
            <button key={s.v}
              onClick={() => setF((p) => ({ ...p, status: s.v }))}
              className={`p-3 rounded-[10px] border text-left transition-colors ${
                f.status === s.v
                  ? "bg-[#1a3524] border-[#39FF14]/50"
                  : "bg-[#121815] border-[#3A4640] hover:border-[#39FF14]/30"
              }`}
              data-testid={`stat-chip-${s.v}`}
            >
              <div className="text-xs text-[#8A8F98] uppercase tracking-wider">{s.label}</div>
              <div className="text-2xl font-heading text-[#F8F9FA] mt-1">
                {stats.by_status?.[s.v] ?? 0}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="p-4 bg-[#121815] border border-[#3A4640] rounded-[10px] space-y-3" data-testid="enq-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
            <Input
              placeholder="Search name, mobile, enquiry ID, product…"
              value={f.q}
              onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))}
              className="pl-9 bg-[#0B0F0C] border-[#3A4640]"
              data-testid="enq-search-input"
            />
          </div>
          <FilterSelect label="Customer Type" value={f.customer_type}
            onChange={(v) => setF((p) => ({ ...p, customer_type: v }))}
            options={CUST_TYPES} testid="filter-cust-type" />
          <FilterSelect label="Type" value={f.enquiry_type}
            onChange={(v) => setF((p) => ({ ...p, enquiry_type: v }))}
            options={TYPES} testid="filter-type" />
          <FilterSelect label="Source" value={f.source}
            onChange={(v) => setF((p) => ({ ...p, source: v }))}
            options={SOURCES} testid="filter-source" />
          <FilterSelect label="Assigned" value={f.assigned_to}
            onChange={(v) => setF((p) => ({ ...p, assigned_to: v }))}
            options={[
              { v: "all", label: "All" },
              ...assignees.map((u) => ({ v: u.id, label: u.name || u.email })),
            ]} testid="filter-assigned" />
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
            <X size={14} className="mr-1" /> Clear
          </Button>
          <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-xs text-[#8A8F98] uppercase tracking-wider">Date range</div>
          <Input type="date" value={f.date_from}
                 onChange={(e) => setF((p) => ({ ...p, date_from: e.target.value }))}
                 className="w-[160px] bg-[#0B0F0C] border-[#3A4640]"
                 data-testid="filter-date-from" />
          <span className="text-[#8A8F98]">→</span>
          <Input type="date" value={f.date_to}
                 onChange={(e) => setF((p) => ({ ...p, date_to: e.target.value }))}
                 className="w-[160px] bg-[#0B0F0C] border-[#3A4640]"
                 data-testid="filter-date-to" />
          <div className="ml-auto text-xs text-[#8A8F98]">
            {total} total {total === 1 ? "enquiry" : "enquiries"}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="rounded-[10px] border border-[#3A4640] overflow-hidden">
        <table className="w-full text-sm" data-testid="enquiries-table">
          <thead className="bg-[#121815] text-[#8A8F98] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Enquiry</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Assigned</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Follow-up</th>
              <th className="text-left px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-[#8A8F98]">
                <Loader2 className="inline animate-spin" /> Loading…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-[#8A8F98]">
                No enquiries match these filters.
              </td></tr>
            )}
            {!loading && items.map((e) => (
              <tr
                key={e.id}
                onClick={() => setOpenId(e.id)}
                className="border-t border-[#3A4640] hover:bg-[#121815]/60 cursor-pointer"
                data-testid={`enq-row-${e.id}`}
              >
                <td className="px-3 py-2 font-mono text-xs text-[#39FF14]">{e.enquiry_no}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{e.customer_name}</div>
                  <div className="text-xs text-[#8A8F98] flex items-center gap-2">
                    <Phone size={10}/> {e.customer_phone || "—"}
                    <CustomerTypePill type={e.customer_type_derived} />
                  </div>
                </td>
                <td className="px-3 py-2">{titleCase(e.enquiry_type)}</td>
                <td className="px-3 py-2">
                  <div>{e.product_model || "—"}</div>
                  {e.product_category && <div className="text-xs text-[#8A8F98]">{e.product_category}</div>}
                </td>
                <td className="px-3 py-2 text-xs">{e.assigned_to_name || <span className="text-[#8A8F98]">—</span>}</td>
                <td className="px-3 py-2"><StatusPill status={e.status} /></td>
                <td className="px-3 py-2 text-xs">
                  {e.follow_up_date || <span className="text-[#8A8F98]">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-[#8A8F98]">{formatDate(e.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1} data-testid="pager-prev">Prev</Button>
          <span className="text-xs text-[#8A8F98]">Page {page} of {Math.ceil(total / limit)}</span>
          <Button variant="ghost" size="sm" onClick={() => setPage((p) => p + 1)}
                  disabled={page * limit >= total} data-testid="pager-next">Next</Button>
        </div>
      )}

      <EnquiryFormModal
        open={showNew}
        assignees={assignees}
        onClose={() => setShowNew(false)}
        onSaved={() => { load(); loadStats(); }}
      />
      <EnquiryDrawer
        enqId={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => { load(); loadStats(); }}
        assignees={assignees}
        canFull={canFull}
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-[#8A8F98] uppercase tracking-wider hidden md:inline">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[160px] bg-[#0B0F0C] border-[#3A4640]" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#121815] border-[#3A4640] max-h-[260px]">
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
